// Phase 2 P2-6 — useBankrollState hook.
//
// Read access to the authenticated user's Bankroll Advisory profile +
// the calibrated daily/weekly stake recommendation from the
// suggest_daily_stake() server RPC.
//
// Schema (Phase 2):
//   - user_bankrolls          — per-user profile (total, risk_profile, caps)
//   - bankroll_ledger         — append-only event log (deposits, P&L, transfers)
//   - vw_bankroll_state       — derived 30d P&L + lifetime deposits
//   - suggest_daily_stake()   — calibrated recommendation incl. pull-back
//
// Truth contract:
//   - Reads only. Writes go through record_bankroll_event RPC (security
//     definer) — exposed via separate hooks when activated.
//   - Recommendation is null when the user has no bankroll profile yet
//     (i.e. has not "declared" their bankroll). The page renders an
//     empty-state declaration prompt in that case.

import { useCallback, useMemo } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useComposedQuery } from "../data/useTableQuery.ts";

export type RiskProfile = "conservative" | "moderate" | "aggressive" | "custom";

export interface BankrollStateRow {
  user_id: string;
  total_bankroll: number;
  currency: string;
  risk_profile: RiskProfile;
  is_active: boolean;
  pl_30d: number;
  lifetime_deposits: number;
  lifetime_withdrawals: number;
}

export interface BankrollProfileRow {
  user_id: string;
  total_bankroll: number;
  currency: string;
  risk_profile: RiskProfile;
  max_daily_loss: number | null;
  max_weekly_loss: number | null;
  max_single_stake_pct: number | null;
  is_active: boolean;
  paused_until: string | null;
  notes: string | null;
}

export interface BankrollLedgerRow {
  id: string;
  user_id: string;
  entry_kind:
    | "deposit"
    | "withdrawal"
    | "win"
    | "loss"
    | "adjustment"
    | "transfer_to_investment";
  amount: number;
  balance_after: number;
  reference_id: string | null;
  reference_type: string | null;
  notes: string | null;
  created_at: string;
}

export interface DailyStakeRecommendation {
  user_id: string;
  currency: string;
  total_bankroll: number;
  risk_profile: RiskProfile;
  base_pct: number;
  pull_back_factor: number;
  recent_7d_pl: number;
  recommended_daily_stake: number;
  recommended_weekly_stake: number;
  rationale: string;
  error?: string;
}

export interface BankrollState {
  profile: BankrollProfileRow | null;
  state: BankrollStateRow | null;
  ledger: BankrollLedgerRow[];
  recommendation: DailyStakeRecommendation | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Composite the read returns; carries the hook's aggregated-error rule
// (errs.join(" · ")) and recommendation-nulled-on-rpc-error rule.
interface BankrollReadResult {
  profile: BankrollProfileRow | null;
  state: BankrollStateRow | null;
  ledger: BankrollLedgerRow[];
  recommendation: DailyStakeRecommendation | null;
  error: string | null;
}

const EMPTY_LEDGER: BankrollLedgerRow[] = [];

export function useBankrollState(): BankrollState {
  const { user } = useAuthSession();
  const enabled = Boolean(user);
  const userId = user?.id ?? null;

  const q = useComposedQuery<BankrollReadResult>(
    ["bankroll-state", userId],
    async (client): Promise<BankrollReadResult> => {
      // Generated Database types don't yet include user_bankrolls / bankroll_ledger
      // / vw_bankroll_state. Cast to `any` for the new reads. Once `db:gen-types`
      // is run after migrations, the cast can be removed.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = client as any;

      const [profileRes, stateRes, ledgerRes, recRes] = await Promise.all([
        sb
          .from("user_bankrolls")
          .select(
            "user_id, total_bankroll, currency, risk_profile, max_daily_loss, max_weekly_loss, max_single_stake_pct, is_active, paused_until, notes",
          )
          .eq("user_id", userId)
          .maybeSingle(),
        sb
          .from("vw_bankroll_state")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
        sb
          .from("bankroll_ledger")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(20),
        sb.rpc("suggest_daily_stake", { p_user_id: userId }),
      ]);

      const errs = [profileRes.error, stateRes.error, ledgerRes.error, recRes.error]
        .filter((e: { message?: string } | null): e is { message: string } => Boolean(e && e.message))
        .map((e: { message: string }) => e.message);

      const recData = recRes.data as DailyStakeRecommendation | null;
      return {
        profile: (profileRes.data as BankrollProfileRow | null) ?? null,
        state: (stateRes.data as BankrollStateRow | null) ?? null,
        ledger: (ledgerRes.data ?? []) as BankrollLedgerRow[],
        recommendation: recData && !recData.error ? recData : null,
        error: errs.length > 0 ? errs.join(" · ") : null,
      };
    },
    { enabled },
  );

  const profile = enabled ? (q.data?.profile ?? null) : null;
  const state = enabled ? (q.data?.state ?? null) : null;
  const ledger = enabled ? (q.data?.ledger ?? EMPTY_LEDGER) : EMPTY_LEDGER;
  const recommendation = enabled ? (q.data?.recommendation ?? null) : null;
  const error = !enabled ? null : (q.data?.error ?? (q.error ? q.error.message : null));
  const loading = enabled ? q.isLoading : false;

  const refetch = useCallback(async (): Promise<void> => {
    await q.refetch();
  }, [q]);

  return useMemo<BankrollState>(
    () => ({ profile, state, ledger, recommendation, loading, error, refetch }),
    [profile, state, ledger, recommendation, loading, error, refetch],
  );
}
